// Product Templates for Quick Business Setup
// Pre-filled product catalogs for different business types

import { Product } from '../interfaces/Product';

export interface ProductTemplate {
  businessType: string;
  categoryId: string;
  categoryName: string;
  icon: string;
  products: Omit<Product, 'id' | 'businessId' | 'createdAt' | 'updatedAt'>[];
}

export const PRODUCT_TEMPLATES: ProductTemplate[] = [
  // Halal Meat & Butcher Template
  {
    businessType: 'halal_meat',
    categoryId: 'halal_meat',
    categoryName: 'Halal Meat & Butcher',
    icon: '🥩',
    products: [
      // Beef Products
      {
        name: 'Halal Beef Steak',
        description: 'Premium halal beef steak, hand-cut',
        price: 12.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Beef',
        isActive: true,
        isInStock: true,
        stockQuantity: 50,
        lowStockThreshold: 10,
        autoRestock: true,
        halalCertified: true,
        icon: '🥩',
        tags: ['beef', 'steak', 'premium', 'halal'],
        preparationTime: 0,
        specifications: {
          cut: 'Ribeye',
          grade: 'Choice',
          freshness: 'Fresh Daily'
        }
      },
      {
        name: 'Halal Ground Beef',
        description: 'Fresh halal ground beef, 80/20 lean',
        price: 8.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Beef',
        isActive: true,
        isInStock: true,
        stockQuantity: 100,
        lowStockThreshold: 20,
        autoRestock: true,
        halalCertified: true,
        icon: '🥩',
        tags: ['beef', 'ground', 'halal'],
        preparationTime: 0,
        specifications: {
          leanRatio: '80/20',
          freshness: 'Fresh Daily'
        }
      },
      {
        name: 'Halal Beef Ribs',
        description: 'Tender halal beef short ribs',
        price: 10.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Beef',
        isActive: true,
        isInStock: true,
        stockQuantity: 40,
        lowStockThreshold: 10,
        autoRestock: true,
        halalCertified: true,
        icon: '🍖',
        tags: ['beef', 'ribs', 'halal', 'bbq'],
        preparationTime: 0,
        specifications: {
          cuts: ['Short Ribs']
        }
      },
      {
        name: 'Halal Beef Roast',
        description: 'Premium halal beef roast',
        price: 11.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Beef',
        isActive: true,
        isInStock: true,
        stockQuantity: 30,
        lowStockThreshold: 8,
        autoRestock: true,
        halalCertified: true,
        icon: '🥩',
        tags: ['beef', 'roast', 'halal'],
        preparationTime: 0,
        specifications: {
          cut: 'Chuck Roast',
          origin: 'Local'
        }
      },
      
      // Chicken Products
      {
        name: 'Halal Chicken Breast',
        description: 'Fresh halal chicken breast, boneless skinless',
        price: 6.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Chicken',
        isActive: true,
        isInStock: true,
        stockQuantity: 120,
        lowStockThreshold: 25,
        autoRestock: true,
        halalCertified: true,
        icon: '🍗',
        tags: ['chicken', 'breast', 'boneless', 'halal'],
        preparationTime: 0,
        specifications: {
          type: 'Boneless Skinless',
          freshness: 'Daily'
        }
      },
      {
        name: 'Halal Chicken Thighs',
        description: 'Juicy halal chicken thighs',
        price: 5.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Chicken',
        isActive: true,
        isInStock: true,
        stockQuantity: 80,
        lowStockThreshold: 20,
        autoRestock: true,
        halalCertified: true,
        icon: '🍗',
        tags: ['chicken', 'thighs', 'halal'],
        preparationTime: 0,
        specifications: {
          type: 'Bone-in',
          origin: 'Farm Fresh'
        }
      },
      {
        name: 'Halal Whole Chicken',
        description: 'Fresh whole halal chicken',
        price: 4.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Chicken',
        isActive: true,
        isInStock: true,
        stockQuantity: 50,
        lowStockThreshold: 10,
        autoRestock: true,
        halalCertified: true,
        icon: '🍗',
        tags: ['chicken', 'whole', 'halal'],
        preparationTime: 0,
        specifications: {
          weight: '3-4 lbs average',
          origin: 'Farm Fresh'
        }
      },
      {
        name: 'Halal Chicken Wings',
        description: 'Fresh halal chicken wings',
        price: 7.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Chicken',
        isActive: true,
        isInStock: true,
        stockQuantity: 60,
        lowStockThreshold: 15,
        autoRestock: true,
        halalCertified: true,
        icon: '🍗',
        tags: ['chicken', 'wings', 'halal', 'party'],
        preparationTime: 0,
        specifications: {
          type: 'Party Wings',
          origin: 'Farm Fresh'
        }
      },
      
      // Lamb Products
      {
        name: 'Halal Lamb Chops',
        description: 'Premium halal lamb chops',
        price: 15.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Lamb',
        isActive: true,
        isInStock: true,
        stockQuantity: 35,
        lowStockThreshold: 8,
        autoRestock: true,
        halalCertified: true,
        icon: '🐑',
        tags: ['lamb', 'chops', 'premium', 'halal'],
        preparationTime: 0,
        specifications: {
          cut: 'Loin Chops',
          grade: 'Premium'
        }
      },
      {
        name: 'Halal Lamb Leg',
        description: 'Whole halal lamb leg',
        price: 13.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Lamb',
        isActive: true,
        isInStock: true,
        stockQuantity: 20,
        lowStockThreshold: 5,
        autoRestock: true,
        halalCertified: true,
        icon: '🐑',
        tags: ['lamb', 'leg', 'whole', 'halal'],
        preparationTime: 0,
        specifications: {
          cut: 'Whole Leg',
          weight: '5-7 lbs average'
        }
      },
      {
        name: 'Halal Ground Lamb',
        description: 'Fresh halal ground lamb',
        price: 11.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Lamb',
        isActive: true,
        isInStock: true,
        stockQuantity: 45,
        lowStockThreshold: 10,
        autoRestock: true,
        halalCertified: true,
        icon: '🐑',
        tags: ['lamb', 'ground', 'halal'],
        preparationTime: 0,
        specifications: {
          leanRatio: '85/15',
          freshness: 'Fresh Daily'
        }
      },
      
      // Goat Products
      {
        name: 'Halal Goat Meat',
        description: 'Fresh halal goat meat, bone-in',
        price: 12.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Goat',
        isActive: true,
        isInStock: true,
        stockQuantity: 25,
        lowStockThreshold: 5,
        autoRestock: true,
        halalCertified: true,
        icon: '🐐',
        tags: ['goat', 'meat', 'halal'],
        preparationTime: 0,
        specifications: {
          cut: 'Mixed Cuts',
          origin: 'Farm Fresh'
        }
      },
      {
        name: 'Halal Goat Chops',
        description: 'Premium halal goat chops',
        price: 14.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Goat',
        isActive: true,
        isInStock: true,
        stockQuantity: 20,
        lowStockThreshold: 5,
        autoRestock: true,
        halalCertified: true,
        icon: '🐐',
        tags: ['goat', 'chops', 'premium', 'halal'],
        preparationTime: 0,
        specifications: {
          cut: 'Rib Chops',
          grade: 'Premium'
        }
      },
      
      // Specialty Products
      {
        name: 'Halal Sausages',
        description: 'Halal beef sausages, various flavors',
        price: 9.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Specialty',
        isActive: true,
        isInStock: true,
        stockQuantity: 40,
        lowStockThreshold: 10,
        autoRestock: true,
        halalCertified: true,
        icon: '🌭',
        tags: ['sausage', 'beef', 'halal', 'specialty'],
        preparationTime: 0,
        specifications: {
          flavors: 'Original, Spicy, Italian',
          casing: 'Natural'
        }
      },
      {
        name: 'Halal Beef Bacon',
        description: 'Halal beef bacon strips',
        price: 10.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Specialty',
        isActive: true,
        isInStock: true,
        stockQuantity: 35,
        lowStockThreshold: 8,
        autoRestock: true,
        halalCertified: true,
        icon: '🥓',
        tags: ['bacon', 'beef', 'halal', 'breakfast'],
        preparationTime: 0,
        specifications: {
          type: 'Smoked',
          slicing: 'Thick Cut'
        }
      },
      {
        name: 'Halal Kabob Meat',
        description: 'Pre-cut halal kabob meat, ready to grill',
        price: 13.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Specialty',
        isActive: true,
        isInStock: true,
        stockQuantity: 50,
        lowStockThreshold: 12,
        autoRestock: true,
        halalCertified: true,
        icon: '🍖',
        tags: ['kabob', 'grill', 'halal', 'bbq'],
        preparationTime: 0,
        specifications: {
          meat: 'Beef or Lamb',
          cut: 'Pre-cut cubes',
          marinade: 'Optional'
        }
      },
    ]
  },
  
  // Halal Grocery Template
  {
    businessType: 'halal_grocery',
    categoryId: 'halal_grocery',
    categoryName: 'Halal Grocery Store',
    icon: '🛒',
    products: [
      {
        name: 'Basmati Rice',
        description: 'Premium halal basmati rice, 10 lb bag',
        price: 15.99,
        unit: 'bag',
        category: 'Grains',
        subcategory: 'Rice',
        isActive: true,
        isInStock: true,
        stockQuantity: 100,
        lowStockThreshold: 20,
        autoRestock: true,
        halalCertified: true,
        icon: '🌾',
        tags: ['rice', 'basmati', 'halal', 'grain'],
        preparationTime: 0,
        weight: 10,
        specifications: {
          size: '10 lb bag',
          quality: 'Premium Long Grain'
        }
      },
      {
        name: 'Halal Frozen Samosas',
        description: 'Halal vegetable samosas, 20-pack',
        price: 8.99,
        unit: 'pack',
        category: 'Frozen',
        subcategory: 'Snacks',
        isActive: true,
        isInStock: true,
        stockQuantity: 50,
        lowStockThreshold: 10,
        autoRestock: true,
        halalCertified: true,
        icon: '🥟',
        tags: ['samosa', 'frozen', 'halal', 'snack'],
        preparationTime: 15,
        specifications: {
          quantity: '20 pieces',
          type: 'Vegetable',
          cooking: 'Bake or Fry'
        }
      },
      {
        name: 'Halal Spice Mix',
        description: 'Authentic halal spice blend',
        price: 4.99,
        unit: 'jar',
        category: 'Spices',
        subcategory: 'Seasonings',
        isActive: true,
        isInStock: true,
        stockQuantity: 75,
        lowStockThreshold: 15,
        autoRestock: true,
        halalCertified: true,
        icon: '🌶️',
        tags: ['spice', 'seasoning', 'halal'],
        preparationTime: 0,
        weight: 0.5,
        specifications: {
          size: '8 oz jar',
          blend: 'Traditional',
          spiceLevel: 'Medium'
        }
      },
      {
        name: 'Halal Dates',
        description: 'Premium Medjool dates, 2 lb box',
        price: 12.99,
        unit: 'box',
        category: 'Produce',
        subcategory: 'Fruits',
        isActive: true,
        isInStock: true,
        stockQuantity: 60,
        lowStockThreshold: 15,
        autoRestock: true,
        halalCertified: true,
        icon: '🌴',
        tags: ['dates', 'fruit', 'halal', 'premium'],
        preparationTime: 0,
        weight: 2,
        specifications: {
          variety: 'Medjool',
          size: 'Large'
        }
      },
      {
        name: 'Halal Olive Oil',
        description: 'Extra virgin halal olive oil, 1 liter',
        price: 14.99,
        unit: 'bottle',
        category: 'Oils',
        subcategory: 'Cooking Oils',
        isActive: true,
        isInStock: true,
        stockQuantity: 80,
        lowStockThreshold: 20,
        autoRestock: true,
        halalCertified: true,
        icon: '🫒',
        tags: ['olive oil', 'halal', 'cooking'],
        preparationTime: 0,
        specifications: {
          size: '1 liter',
          grade: 'Extra Virgin',
        }
      },
    ]
  },
  
  // Halal Bakery Template
  {
    businessType: 'halal_bakery',
    categoryId: 'halal_bakery',
    categoryName: 'Halal Bakery',
    icon: '🥖',
    products: [
      {
        name: 'Fresh Pita Bread',
        description: 'Soft halal pita bread, baked daily',
        price: 2.99,
        unit: 'dozen',
        category: 'Bread',
        subcategory: 'Pita',
        isActive: true,
        isInStock: true,
        stockQuantity: 100,
        lowStockThreshold: 20,
        autoRestock: true,
        halalCertified: true,
        icon: '🥖',
        tags: ['pita', 'bread', 'halal', 'fresh'],
        preparationTime: 30,
        specifications: {
          quantity: '12 pieces',
          size: 'Regular',
          freshness: 'Baked Daily'
        }
      },
      {
        name: 'Halal Baklava',
        description: 'Traditional halal baklava, assorted',
        price: 18.99,
        unit: 'box',
        category: 'Pastries',
        subcategory: 'Desserts',
        isActive: true,
        isInStock: true,
        stockQuantity: 40,
        lowStockThreshold: 10,
        autoRestock: true,
        halalCertified: true,
        icon: '🥐',
        tags: ['baklava', 'pastry', 'halal', 'dessert'],
        preparationTime: 60,
        specifications: {
          quantity: '1 lb box',
          variety: 'Assorted',
          nuts: 'Pistachios & Walnuts'
        }
      },
      {
        name: 'Halal Croissants',
        description: 'Butter halal croissants, 6-pack',
        price: 5.99,
        unit: 'pack',
        category: 'Pastries',
        subcategory: 'Bread',
        isActive: true,
        isInStock: true,
        stockQuantity: 50,
        lowStockThreshold: 12,
        autoRestock: true,
        halalCertified: true,
        icon: '🥐',
        tags: ['croissant', 'pastry', 'halal', 'breakfast'],
        preparationTime: 45,
        specifications: {
          quantity: '6 pieces',
          type: 'Butter',
          freshness: 'Baked Fresh'
        }
      },
      {
        name: 'Halal Wedding Cake',
        description: 'Custom halal wedding cake (order ahead)',
        price: 150.00,
        unit: 'cake',
        category: 'Cakes',
        subcategory: 'Custom Orders',
        isActive: true,
        isInStock: true,
        stockQuantity: 5,
        lowStockThreshold: 1,
        autoRestock: false,
        halalCertified: true,
        icon: '🎂',
        tags: ['cake', 'wedding', 'halal', 'custom'],
        preparationTime: 2880, // 48 hours advance notice
        specifications: {
          size: '3-tier standard',
          serves: '50-75 people',
          flavors: 'Vanilla, Chocolate, Red Velvet',
          notice: '48 hours advance order required'
        }
      },
      {
        name: 'Halal Cookies',
        description: 'Assorted halal cookies, 1 dozen',
        price: 7.99,
        unit: 'dozen',
        category: 'Baked Goods',
        subcategory: 'Cookies',
        isActive: true,
        isInStock: true,
        stockQuantity: 80,
        lowStockThreshold: 20,
        autoRestock: true,
        halalCertified: true,
        icon: '🍪',
        tags: ['cookies', 'halal', 'dessert', 'gift'],
        preparationTime: 0,
        specifications: {
          quantity: '12 cookies',
          variety: 'Chocolate Chip, Sugar, Oatmeal',
          freshness: 'Baked Daily'
        }
      },
    ]
  },
  
  // Halal Restaurant Template
  {
    businessType: 'halal_restaurant',
    categoryId: 'halal_restaurant',
    categoryName: 'Halal Restaurant',
    icon: '🍽️',
    products: [
      {
        name: 'Chicken Biryani',
        description: 'Traditional halal chicken biryani',
        price: 12.99,
        unit: 'plate',
        category: 'Entrees',
        subcategory: 'Rice Dishes',
        isActive: true,
        isInStock: true,
        stockQuantity: 999, // Service item, always available
        lowStockThreshold: 0,
        autoRestock: false,
        halalCertified: true,
        icon: '🍛',
        tags: ['biryani', 'chicken', 'halal', 'rice'],
        preparationTime: 20,
        specifications: {
          spiceLevel: 'Medium',
          serves: '1 person',
          sides: 'Raita included'
        }
      },
      {
        name: 'Lamb Kabob Platter',
        description: 'Grilled halal lamb kabobs with rice',
        price: 16.99,
        unit: 'platter',
        category: 'Entrees',
        subcategory: 'Grilled',
        isActive: true,
        isInStock: true,
        stockQuantity: 999,
        lowStockThreshold: 0,
        autoRestock: false,
        halalCertified: true,
        icon: '🍖',
        tags: ['kabob', 'lamb', 'halal', 'grill'],
        preparationTime: 15,
        specifications: {
          pieces: '4 skewers',
          sides: 'Rice, Salad, Sauce',
          serves: '1-2 people'
        }
      },
      {
        name: 'Falafel Wrap',
        description: 'Fresh halal falafel in pita',
        price: 7.99,
        unit: 'wrap',
        category: 'Sandwiches',
        subcategory: 'Wraps',
        isActive: true,
        isInStock: true,
        stockQuantity: 999,
        lowStockThreshold: 0,
        autoRestock: false,
        halalCertified: true,
        icon: '🌯',
        tags: ['falafel', 'wrap', 'halal', 'vegetarian'],
        preparationTime: 10,
        specifications: {
          type: 'Vegetarian',
          toppings: 'Tahini, Lettuce, Tomato',
          spiceLevel: 'Mild'
        }
      },
      {
        name: 'Shawarma Plate',
        description: 'Chicken or beef shawarma plate',
        price: 13.99,
        unit: 'plate',
        category: 'Entrees',
        subcategory: 'Shawarma',
        isActive: true,
        isInStock: true,
        stockQuantity: 999,
        lowStockThreshold: 0,
        autoRestock: false,
        halalCertified: true,
        icon: '🥙',
        tags: ['shawarma', 'halal', 'chicken', 'beef'],
        preparationTime: 12,
        specifications: {
          protein: 'Chicken or Beef',
          sides: 'Rice, Hummus, Pita',
          serves: '1 person'
        }
      },
      {
        name: 'Hummus & Pita',
        description: 'Fresh halal hummus with warm pita',
        price: 5.99,
        unit: 'plate',
        category: 'Appetizers',
        subcategory: 'Dips',
        isActive: true,
        isInStock: true,
        stockQuantity: 999,
        lowStockThreshold: 0,
        autoRestock: false,
        halalCertified: true,
        icon: '🫘',
        tags: ['hummus', 'pita', 'halal', 'appetizer'],
        preparationTime: 5,
        specifications: {
          size: 'Large',
          toppings: 'Olive Oil, Paprika',
          pita: '2 pieces included'
        }
      },
    ]
  },
  
  // General Butcher Shop Template (Non-Halal)
  {
    businessType: 'butcher',
    categoryId: 'food_beverage',
    categoryName: 'General Butcher Shop',
    icon: '🥩',
    products: [
      // Beef Products
      {
        name: 'Beef Ribeye Steak',
        description: 'Premium ribeye steak, hand-cut',
        price: 14.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Beef',
        isActive: true,
        isInStock: true,
        stockQuantity: 50,
        lowStockThreshold: 10,
        autoRestock: true,
        halalCertified: false,
        icon: '🥩',
        tags: ['beef', 'steak', 'ribeye', 'premium'],
        preparationTime: 0,
        specifications: {
          cut: 'Ribeye',
          grade: 'Choice',
          freshness: 'Fresh Daily'
        }
      },
      {
        name: 'Ground Beef',
        description: 'Fresh ground beef, 80/20 lean',
        price: 6.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Beef',
        isActive: true,
        isInStock: true,
        stockQuantity: 100,
        lowStockThreshold: 20,
        autoRestock: true,
        halalCertified: false,
        icon: '🥩',
        tags: ['beef', 'ground', 'burgers'],
        preparationTime: 0,
        specifications: {
          leanRatio: '80/20',
          freshness: 'Fresh Daily'
        }
      },
      {
        name: 'Beef Short Ribs',
        description: 'Tender beef short ribs, perfect for BBQ',
        price: 11.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Beef',
        isActive: true,
        isInStock: true,
        stockQuantity: 40,
        lowStockThreshold: 10,
        autoRestock: true,
        halalCertified: false,
        icon: '🍖',
        tags: ['beef', 'ribs', 'bbq'],
        preparationTime: 0,
        specifications: {
          cuts: ['Short Ribs']
        }
      },
      {
        name: 'Sirloin Steak',
        description: 'Lean and flavorful sirloin steak',
        price: 10.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Beef',
        isActive: true,
        isInStock: true,
        stockQuantity: 45,
        lowStockThreshold: 10,
        autoRestock: true,
        halalCertified: false,
        icon: '🥩',
        tags: ['beef', 'steak', 'sirloin'],
        preparationTime: 0,
        specifications: {
          cut: 'Sirloin',
          grade: 'Choice'
        }
      },
      
      // Pork Products
      {
        name: 'Pork Chops',
        description: 'Thick-cut pork chops',
        price: 7.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Pork',
        isActive: true,
        isInStock: true,
        stockQuantity: 60,
        lowStockThreshold: 15,
        autoRestock: true,
        halalCertified: false,
        icon: '🥓',
        tags: ['pork', 'chops', 'grill'],
        preparationTime: 0,
        specifications: {
          cut: 'Center Cut',
          thickness: '1 inch'
        }
      },
      {
        name: 'Baby Back Ribs',
        description: 'Premium baby back pork ribs',
        price: 9.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Pork',
        isActive: true,
        isInStock: true,
        stockQuantity: 35,
        lowStockThreshold: 8,
        autoRestock: true,
        halalCertified: false,
        icon: '🍖',
        tags: ['pork', 'ribs', 'bbq', 'baby back'],
        preparationTime: 0,
        specifications: {
          cut: 'Baby Back',
          origin: 'Local'
        }
      },
      {
        name: 'Ground Pork',
        description: 'Fresh ground pork for sausages and meatballs',
        price: 5.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Pork',
        isActive: true,
        isInStock: true,
        stockQuantity: 50,
        lowStockThreshold: 15,
        autoRestock: true,
        halalCertified: false,
        icon: '🥓',
        tags: ['pork', 'ground', 'sausage'],
        preparationTime: 0,
        specifications: {
          leanRatio: '80/20',
          freshness: 'Fresh Daily'
        }
      },
      {
        name: 'Pork Tenderloin',
        description: 'Lean and tender pork tenderloin',
        price: 8.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Pork',
        isActive: true,
        isInStock: true,
        stockQuantity: 30,
        lowStockThreshold: 8,
        autoRestock: true,
        halalCertified: false,
        icon: '🥓',
        tags: ['pork', 'tenderloin', 'lean'],
        preparationTime: 0,
        specifications: {
          cut: 'Tenderloin',
          origin: 'Local'
        }
      },
      
      // Chicken Products
      {
        name: 'Chicken Breast',
        description: 'Fresh chicken breast, boneless skinless',
        price: 5.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Chicken',
        isActive: true,
        isInStock: true,
        stockQuantity: 120,
        lowStockThreshold: 25,
        autoRestock: true,
        halalCertified: false,
        icon: '🍗',
        tags: ['chicken', 'breast', 'boneless'],
        preparationTime: 0,
        specifications: {
          type: 'Boneless Skinless',
          freshness: 'Daily'
        }
      },
      {
        name: 'Chicken Thighs',
        description: 'Juicy chicken thighs',
        price: 4.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Chicken',
        isActive: true,
        isInStock: true,
        stockQuantity: 80,
        lowStockThreshold: 20,
        autoRestock: true,
        halalCertified: false,
        icon: '🍗',
        tags: ['chicken', 'thighs'],
        preparationTime: 0,
        specifications: {
          type: 'Bone-in',
          origin: 'Farm Fresh'
        }
      },
      {
        name: 'Whole Chicken',
        description: 'Fresh whole chicken',
        price: 3.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Chicken',
        isActive: true,
        isInStock: true,
        stockQuantity: 50,
        lowStockThreshold: 10,
        autoRestock: true,
        halalCertified: false,
        icon: '🍗',
        tags: ['chicken', 'whole', 'roasting'],
        preparationTime: 0,
        specifications: {
          weight: '3-4 lbs average',
          origin: 'Farm Fresh'
        }
      },
      {
        name: 'Chicken Wings',
        description: 'Fresh chicken wings, perfect for parties',
        price: 6.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Chicken',
        isActive: true,
        isInStock: true,
        stockQuantity: 60,
        lowStockThreshold: 15,
        autoRestock: true,
        halalCertified: false,
        icon: '🍗',
        tags: ['chicken', 'wings', 'party', 'bbq'],
        preparationTime: 0,
        specifications: {
          type: 'Party Wings',
          origin: 'Farm Fresh'
        }
      },
      
      // Specialty Items
      {
        name: 'Italian Sausage',
        description: 'Fresh Italian sausage links',
        price: 7.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Specialty',
        isActive: true,
        isInStock: true,
        stockQuantity: 40,
        lowStockThreshold: 10,
        autoRestock: true,
        halalCertified: false,
        icon: '🌭',
        tags: ['sausage', 'italian', 'specialty'],
        preparationTime: 0,
        specifications: {
          flavor: 'Sweet or Hot',
          casing: 'Natural'
        }
      },
      {
        name: 'Bacon',
        description: 'Thick-cut smoked bacon',
        price: 8.99,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Specialty',
        isActive: true,
        isInStock: true,
        stockQuantity: 50,
        lowStockThreshold: 12,
        autoRestock: true,
        halalCertified: false,
        icon: '🥓',
        tags: ['bacon', 'pork', 'breakfast', 'smoked'],
        preparationTime: 0,
        specifications: {
          type: 'Smoked',
          slicing: 'Thick Cut'
        }
      },
      {
        name: 'Ground Turkey',
        description: 'Lean ground turkey',
        price: 6.49,
        unit: 'lb',
        category: 'Meat',
        subcategory: 'Poultry',
        isActive: true,
        isInStock: true,
        stockQuantity: 45,
        lowStockThreshold: 12,
        autoRestock: true,
        halalCertified: false,
        icon: '🦃',
        tags: ['turkey', 'ground', 'lean', 'healthy'],
        preparationTime: 0,
        specifications: {
          leanRatio: '93/7',
          freshness: 'Fresh Daily'
        }
      },
    ]
  },
  
  // Bakery Template
  {
    businessType: 'bakery',
    categoryId: 'food_beverage',
    categoryName: 'Bakery',
    icon: '🍞',
    products: [
      // Breads
      {
        name: 'Sourdough Bread',
        description: 'Artisan sourdough bread, baked fresh daily',
        price: 5.99,
        unit: 'loaf',
        category: 'Bread',
        subcategory: 'Artisan',
        isActive: true,
        isInStock: true,
        stockQuantity: 50,
        lowStockThreshold: 10,
        autoRestock: true,
        halalCertified: false,
        icon: '🍞',
        tags: ['bread', 'sourdough', 'artisan', 'fresh'],
        preparationTime: 60,
        specifications: {
          size: 'Large loaf',
          weight: '1.5 lbs',
          freshness: 'Baked Daily'
        }
      },
      {
        name: 'French Baguette',
        description: 'Classic French baguette',
        price: 3.99,
        unit: 'each',
        category: 'Bread',
        subcategory: 'French',
        isActive: true,
        isInStock: true,
        stockQuantity: 80,
        lowStockThreshold: 20,
        autoRestock: true,
        halalCertified: false,
        icon: '🥖',
        tags: ['bread', 'baguette', 'french', 'crispy'],
        preparationTime: 45,
        specifications: {
          length: '24 inches',
          freshness: 'Baked Daily'
        }
      },
      {
        name: 'Whole Wheat Bread',
        description: 'Healthy whole wheat sandwich bread',
        price: 4.99,
        unit: 'loaf',
        category: 'Bread',
        subcategory: 'Sandwich',
        isActive: true,
        isInStock: true,
        stockQuantity: 60,
        lowStockThreshold: 15,
        autoRestock: true,
        halalCertified: false,
        icon: '🍞',
        tags: ['bread', 'whole wheat', 'healthy', 'sandwich'],
        preparationTime: 40,
        specifications: {
          slices: '20 slices',
          weight: '1.5 lbs'
        }
      },
      {
        name: 'Ciabatta Bread',
        description: 'Italian ciabatta bread',
        price: 4.49,
        unit: 'loaf',
        category: 'Bread',
        subcategory: 'Italian',
        isActive: true,
        isInStock: true,
        stockQuantity: 40,
        lowStockThreshold: 10,
        autoRestock: true,
        halalCertified: false,
        icon: '🍞',
        tags: ['bread', 'ciabatta', 'italian', 'rustic'],
        preparationTime: 50,
        specifications: {
          size: 'Medium loaf',
          freshness: 'Baked Daily'
        }
      },
      
      // Pastries
      {
        name: 'Butter Croissants',
        description: 'Flaky butter croissants, 6-pack',
        price: 8.99,
        unit: 'pack',
        category: 'Pastries',
        subcategory: 'French',
        isActive: true,
        isInStock: true,
        stockQuantity: 50,
        lowStockThreshold: 12,
        autoRestock: true,
        halalCertified: false,
        icon: '🥐',
        tags: ['croissant', 'pastry', 'french', 'butter'],
        preparationTime: 90,
        specifications: {
          quantity: '6 pieces',
          type: 'Butter',
          freshness: 'Baked Fresh Daily'
        }
      },
      {
        name: 'Danish Pastries',
        description: 'Assorted Danish pastries, 6-pack',
        price: 10.99,
        unit: 'pack',
        category: 'Pastries',
        subcategory: 'Danish',
        isActive: true,
        isInStock: true,
        stockQuantity: 40,
        lowStockThreshold: 10,
        autoRestock: true,
        halalCertified: false,
        icon: '🥐',
        tags: ['danish', 'pastry', 'fruit', 'cream cheese'],
        preparationTime: 60,
        specifications: {
          quantity: '6 pieces',
          varieties: 'Cherry, Apple, Cream Cheese',
          freshness: 'Baked Fresh'
        }
      },
      {
        name: 'Blueberry Muffins',
        description: 'Fresh blueberry muffins, 6-pack',
        price: 7.99,
        unit: 'pack',
        category: 'Pastries',
        subcategory: 'Muffins',
        isActive: true,
        isInStock: true,
        stockQuantity: 60,
        lowStockThreshold: 15,
        autoRestock: true,
        halalCertified: false,
        icon: '🧁',
        tags: ['muffin', 'blueberry', 'breakfast'],
        preparationTime: 30,
        specifications: {
          quantity: '6 jumbo muffins',
          freshness: 'Baked Daily'
        }
      },
      {
        name: 'Chocolate Chip Muffins',
        description: 'Decadent chocolate chip muffins, 6-pack',
        price: 7.99,
        unit: 'pack',
        category: 'Pastries',
        subcategory: 'Muffins',
        isActive: true,
        isInStock: true,
        stockQuantity: 60,
        lowStockThreshold: 15,
        autoRestock: true,
        halalCertified: false,
        icon: '🧁',
        tags: ['muffin', 'chocolate', 'dessert'],
        preparationTime: 30,
        specifications: {
          quantity: '6 jumbo muffins',
          freshness: 'Baked Daily'
        }
      },
      
      // Cookies & Desserts
      {
        name: 'Chocolate Chip Cookies',
        description: 'Classic chocolate chip cookies, 1 dozen',
        price: 9.99,
        unit: 'dozen',
        category: 'Cookies',
        subcategory: 'Classic',
        isActive: true,
        isInStock: true,
        stockQuantity: 80,
        lowStockThreshold: 20,
        autoRestock: true,
        halalCertified: false,
        icon: '🍪',
        tags: ['cookies', 'chocolate chip', 'classic', 'dessert'],
        preparationTime: 20,
        specifications: {
          quantity: '12 large cookies',
          freshness: 'Baked Fresh'
        }
      },
      {
        name: 'Sugar Cookies',
        description: 'Soft sugar cookies, 1 dozen',
        price: 8.99,
        unit: 'dozen',
        category: 'Cookies',
        subcategory: 'Classic',
        isActive: true,
        isInStock: true,
        stockQuantity: 70,
        lowStockThreshold: 20,
        autoRestock: true,
        halalCertified: false,
        icon: '🍪',
        tags: ['cookies', 'sugar', 'classic'],
        preparationTime: 20,
        specifications: {
          quantity: '12 cookies',
          decoration: 'Plain or Decorated'
        }
      },
      {
        name: 'Birthday Cake',
        description: 'Custom birthday cake (order 24 hours ahead)',
        price: 45.00,
        unit: 'cake',
        category: 'Cakes',
        subcategory: 'Custom',
        isActive: true,
        isInStock: true,
        stockQuantity: 10,
        lowStockThreshold: 2,
        autoRestock: false,
        halalCertified: false,
        icon: '🎂',
        tags: ['cake', 'birthday', 'custom', 'celebration'],
        preparationTime: 1440, // 24 hours
        specifications: {
          size: '8-inch round',
          serves: '8-12 people',
          flavors: 'Vanilla, Chocolate, Red Velvet',
          notice: '24 hours advance order required'
        }
      },
      {
        name: 'Cupcakes',
        description: 'Gourmet cupcakes, 1 dozen',
        price: 24.99,
        unit: 'dozen',
        category: 'Cakes',
        subcategory: 'Cupcakes',
        isActive: true,
        isInStock: true,
        stockQuantity: 40,
        lowStockThreshold: 10,
        autoRestock: true,
        halalCertified: false,
        icon: '🧁',
        tags: ['cupcakes', 'dessert', 'party'],
        preparationTime: 45,
        specifications: {
          quantity: '12 cupcakes',
          flavors: 'Vanilla, Chocolate, Red Velvet',
          frosting: 'Buttercream'
        }
      },
    ]
  },
  
  // Coffee Shop/Cafe Template
  {
    businessType: 'coffee_shop',
    categoryId: 'food_beverage',
    categoryName: 'Coffee Shop / Cafe',
    icon: '☕',
    products: [
      // Hot Drinks
      {
        name: 'Espresso',
        description: 'Rich and bold espresso shot',
        price: 2.99,
        unit: 'cup',
        category: 'Beverages',
        subcategory: 'Hot Coffee',
        isActive: true,
        isInStock: true,
        stockQuantity: 999,
        lowStockThreshold: 0,
        autoRestock: false,
        halalCertified: false,
        icon: '☕',
        tags: ['coffee', 'espresso', 'hot', 'strong'],
        preparationTime: 2,
        specifications: {
          size: 'Single shot',
          caffeine: 'High'
        }
      },
      {
        name: 'Latte',
        description: 'Smooth espresso with steamed milk',
        price: 4.99,
        unit: 'cup',
        category: 'Beverages',
        subcategory: 'Hot Coffee',
        isActive: true,
        isInStock: true,
        stockQuantity: 999,
        lowStockThreshold: 0,
        autoRestock: false,
        halalCertified: false,
        icon: '☕',
        tags: ['coffee', 'latte', 'milk', 'hot'],
        preparationTime: 5,
        specifications: {
          sizes: 'Small, Medium, Large',
          milk: 'Whole, 2%, Oat, Almond'
        }
      },
      {
        name: 'Cappuccino',
        description: 'Espresso with foamed milk',
        price: 4.49,
        unit: 'cup',
        category: 'Beverages',
        subcategory: 'Hot Coffee',
        isActive: true,
        isInStock: true,
        stockQuantity: 999,
        lowStockThreshold: 0,
        autoRestock: false,
        halalCertified: false,
        icon: '☕',
        tags: ['coffee', 'cappuccino', 'foam', 'hot'],
        preparationTime: 5,
        specifications: {
          sizes: 'Small, Medium, Large',
          foam: 'Extra Foamy'
        }
      },
      {
        name: 'Americano',
        description: 'Espresso with hot water',
        price: 3.49,
        unit: 'cup',
        category: 'Beverages',
        subcategory: 'Hot Coffee',
        isActive: true,
        isInStock: true,
        stockQuantity: 999,
        lowStockThreshold: 0,
        autoRestock: false,
        halalCertified: false,
        icon: '☕',
        tags: ['coffee', 'americano', 'hot', 'simple'],
        preparationTime: 3,
        specifications: {
          sizes: 'Small, Medium, Large',
          strength: 'Regular or Extra Shot'
        }
      },
      {
        name: 'Drip Coffee',
        description: 'Fresh brewed coffee',
        price: 2.49,
        unit: 'cup',
        category: 'Beverages',
        subcategory: 'Hot Coffee',
        isActive: true,
        isInStock: true,
        stockQuantity: 999,
        lowStockThreshold: 0,
        autoRestock: false,
        halalCertified: false,
        icon: '☕',
        tags: ['coffee', 'drip', 'hot', 'classic'],
        preparationTime: 2,
        specifications: {
          sizes: 'Small, Medium, Large',
          roast: 'Light, Medium, Dark'
        }
      },
      
      // Cold Drinks
      {
        name: 'Iced Coffee',
        description: 'Refreshing iced coffee',
        price: 3.99,
        unit: 'cup',
        category: 'Beverages',
        subcategory: 'Cold Coffee',
        isActive: true,
        isInStock: true,
        stockQuantity: 999,
        lowStockThreshold: 0,
        autoRestock: false,
        halalCertified: false,
        icon: '🧊',
        tags: ['coffee', 'iced', 'cold', 'refreshing'],
        preparationTime: 3,
        specifications: {
          sizes: 'Small, Medium, Large',
          sweetener: 'Regular, Sugar-Free'
        }
      },
      {
        name: 'Iced Latte',
        description: 'Espresso with cold milk over ice',
        price: 5.49,
        unit: 'cup',
        category: 'Beverages',
        subcategory: 'Cold Coffee',
        isActive: true,
        isInStock: true,
        stockQuantity: 999,
        lowStockThreshold: 0,
        autoRestock: false,
        halalCertified: false,
        icon: '🧊',
        tags: ['coffee', 'iced', 'latte', 'cold'],
        preparationTime: 5,
        specifications: {
          sizes: 'Small, Medium, Large',
          milk: 'Whole, 2%, Oat, Almond'
        }
      },
      {
        name: 'Cold Brew',
        description: 'Smooth cold brew coffee',
        price: 4.99,
        unit: 'cup',
        category: 'Beverages',
        subcategory: 'Cold Coffee',
        isActive: true,
        isInStock: true,
        stockQuantity: 999,
        lowStockThreshold: 0,
        autoRestock: false,
        halalCertified: false,
        icon: '🧊',
        tags: ['coffee', 'cold brew', 'smooth', 'cold'],
        preparationTime: 3,
        specifications: {
          sizes: 'Small, Medium, Large',
          strength: 'Regular or Nitro'
        }
      },
      {
        name: 'Frappe',
        description: 'Blended iced coffee drink',
        price: 5.99,
        unit: 'cup',
        category: 'Beverages',
        subcategory: 'Blended',
        isActive: true,
        isInStock: true,
        stockQuantity: 999,
        lowStockThreshold: 0,
        autoRestock: false,
        halalCertified: false,
        icon: '🥤',
        tags: ['coffee', 'frappe', 'blended', 'sweet'],
        preparationTime: 7,
        specifications: {
          sizes: 'Medium, Large',
          flavors: 'Mocha, Caramel, Vanilla'
        }
      },
      
      // Pastries
      {
        name: 'Croissant',
        description: 'Buttery flaky croissant',
        price: 3.49,
        unit: 'each',
        category: 'Food',
        subcategory: 'Pastries',
        isActive: true,
        isInStock: true,
        stockQuantity: 50,
        lowStockThreshold: 10,
        autoRestock: true,
        halalCertified: false,
        icon: '🥐',
        tags: ['pastry', 'croissant', 'breakfast', 'french'],
        preparationTime: 3,
        specifications: {
          type: 'Plain or Chocolate',
          freshness: 'Baked Fresh Daily'
        }
      },
      {
        name: 'Muffin',
        description: 'Fresh baked muffin',
        price: 3.99,
        unit: 'each',
        category: 'Food',
        subcategory: 'Pastries',
        isActive: true,
        isInStock: true,
        stockQuantity: 60,
        lowStockThreshold: 15,
        autoRestock: true,
        halalCertified: false,
        icon: '🧁',
        tags: ['muffin', 'pastry', 'breakfast'],
        preparationTime: 2,
        specifications: {
          flavors: 'Blueberry, Chocolate, Banana',
          size: 'Large'
        }
      },
      {
        name: 'Scone',
        description: 'Buttery scone with jam',
        price: 3.99,
        unit: 'each',
        category: 'Food',
        subcategory: 'Pastries',
        isActive: true,
        isInStock: true,
        stockQuantity: 40,
        lowStockThreshold: 10,
        autoRestock: true,
        halalCertified: false,
        icon: '🥐',
        tags: ['scone', 'pastry', 'breakfast', 'british'],
        preparationTime: 2,
        specifications: {
          flavors: 'Plain, Blueberry, Cranberry',
          served: 'With butter and jam'
        }
      },
      {
        name: 'Bagel with Cream Cheese',
        description: 'Fresh bagel with cream cheese',
        price: 4.49,
        unit: 'each',
        category: 'Food',
        subcategory: 'Breakfast',
        isActive: true,
        isInStock: true,
        stockQuantity: 50,
        lowStockThreshold: 12,
        autoRestock: true,
        halalCertified: false,
        icon: '🥯',
        tags: ['bagel', 'breakfast', 'cream cheese'],
        preparationTime: 3,
        specifications: {
          types: 'Plain, Sesame, Everything',
          toasted: 'Optional'
        }
      },
      
      // Sandwiches
      {
        name: 'Breakfast Sandwich',
        description: 'Egg, cheese, and bacon on English muffin',
        price: 6.99,
        unit: 'each',
        category: 'Food',
        subcategory: 'Breakfast',
        isActive: true,
        isInStock: true,
        stockQuantity: 999,
        lowStockThreshold: 0,
        autoRestock: false,
        halalCertified: false,
        icon: '🥪',
        tags: ['sandwich', 'breakfast', 'egg', 'bacon'],
        preparationTime: 8,
        specifications: {
          protein: 'Bacon, Sausage, or Ham',
          bread: 'English Muffin or Croissant'
        }
      },
      {
        name: 'Turkey & Swiss Sandwich',
        description: 'Roasted turkey with Swiss cheese',
        price: 8.99,
        unit: 'each',
        category: 'Food',
        subcategory: 'Sandwiches',
        isActive: true,
        isInStock: true,
        stockQuantity: 999,
        lowStockThreshold: 0,
        autoRestock: false,
        halalCertified: false,
        icon: '🥪',
        tags: ['sandwich', 'turkey', 'lunch'],
        preparationTime: 7,
        specifications: {
          bread: 'Ciabatta, Wheat, or White',
          toppings: 'Lettuce, Tomato, Mayo'
        }
      },
      {
        name: 'Veggie Wrap',
        description: 'Fresh vegetables in a whole wheat wrap',
        price: 7.99,
        unit: 'each',
        category: 'Food',
        subcategory: 'Sandwiches',
        isActive: true,
        isInStock: true,
        stockQuantity: 999,
        lowStockThreshold: 0,
        autoRestock: false,
        halalCertified: false,
        icon: '🌯',
        tags: ['wrap', 'vegetarian', 'healthy', 'lunch'],
        preparationTime: 6,
        specifications: {
          vegetables: 'Lettuce, Tomato, Cucumber, Peppers',
          dressing: 'Hummus or Ranch'
        }
      },
      {
        name: 'BLT Sandwich',
        description: 'Bacon, lettuce, tomato on toasted bread',
        price: 7.99,
        unit: 'each',
        category: 'Food',
        subcategory: 'Sandwiches',
        isActive: true,
        isInStock: true,
        stockQuantity: 999,
        lowStockThreshold: 0,
        autoRestock: false,
        halalCertified: false,
        icon: '🥪',
        tags: ['sandwich', 'blt', 'bacon', 'classic'],
        preparationTime: 7,
        specifications: {
          bread: 'White, Wheat, or Sourdough',
          bacon: 'Crispy',
          toasted: 'Yes'
        }
      },
    ]
  },
];

// Helper function to get template by business type
export const getTemplateByBusinessType = (businessType: string): ProductTemplate | undefined => {
  return PRODUCT_TEMPLATES.find(template => template.businessType === businessType);
};

// Helper function to get all available templates
export const getAllTemplates = (): ProductTemplate[] => {
  return PRODUCT_TEMPLATES;
};

// Helper function to create products from template
export const createProductsFromTemplate = (
  template: ProductTemplate,
  businessId: string
): Product[] => {
  return template.products.map((product, index) => ({
    ...product,
    id: `product_${businessId}_${Date.now()}_${index}`,
    businessId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }));
};

export default PRODUCT_TEMPLATES;

